import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import { getByType, movieGenres, tvGenres, yearOptions, type MediaType } from "@/mocks/media";
import { useSeo } from "@/hooks/useSeo";

type SortKey = "updated" | "rating" | "year";

const PAGE_SIZE = 12;

const sortLabels: { key: SortKey; label: string }[] = [
  { key: "updated", label: "按更新时间" },
  { key: "rating", label: "按评分" },
  { key: "year", label: "按年份" },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const mediaType: MediaType | null =
    type === "movie" || type === "tv" ? type : null;

  const [genre, setGenre] = useState(searchParams.get("genre") ?? "全部");
  const [year, setYear] = useState(searchParams.get("year") ?? "全部");
  const [sort, setSort] = useState<SortKey>(
    (searchParams.get("sort") as SortKey) ?? "updated"
  );
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  const isMovie = mediaType === "movie";
  const genres = isMovie ? movieGenres : tvGenres;
  const title = isMovie ? "电影分类" : "电视剧分类";

  useEffect(() => {
    setGenre(searchParams.get("genre") ?? "全部");
    setYear(searchParams.get("year") ?? "全部");
    setSort((searchParams.get("sort") as SortKey) ?? "updated");
  }, [searchParams, type]);

  useSeo({
    title: `${title} - 兔六影院`,
    description: `兔六影院${title}，聚合最新${isMovie ? "电影" : "电视剧"}资源，支持按类型、年份筛选与评分排序，快速找到想看的${isMovie ? "影片" : "剧集"}。`,
    keywords: `兔六影院,${isMovie ? "电影分类,电影在线观看" : "电视剧分类,电视剧观看"},影视筛选,${isMovie ? "热门电影" : "最新电视剧"}`,
  });

  useEffect(() => {
    setPage(1);
  }, [genre, year, sort]);

  const filtered = useMemo(() => {
    if (!mediaType) return [];
    let list = getByType(mediaType);
    if (genre !== "全部") list = list.filter((m) => m.genres.includes(genre));
    if (year !== "全部") list = list.filter((m) => String(m.year) === year);
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "year") sorted.sort((a, b) => b.year - a.year);
    else
      sorted.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    return sorted;
  }, [mediaType, genre, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const applyGenre = (g: string) => {
    setGenre(g);
    const next = new URLSearchParams(searchParams);
    if (g === "全部") next.delete("genre");
    else next.set("genre", g);
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    applyGenre("全部");
    setYear("全部");
    setSort("updated");
    setSearchParams({}, { replace: true });
  };

  if (!mediaType) {
    return (
      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-24 text-center">
        <h1 className="font-heading text-2xl font-black text-foreground-950">
          未找到该分类
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的分类不存在，请从导航重新选择。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-primary-900 py-8 md:py-12">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <nav className="flex items-center gap-1 text-xs text-primary-200">
            <Link to="/" className="hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{isMovie ? "电影" : "电视剧"}</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
            {title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-primary-200">
            {isMovie
              ? "汇集动作、科幻、悬疑、爱情等多种类型的电影作品，支持按类型与年份精准筛选，按更新时间或评分排序浏览。"
              : "涵盖都市、古装、悬疑、刑侦等热门题材的电视剧作品，支持按类型与年份筛选，快速定位你感兴趣的剧集。"}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="hidden md:block">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-700">类型</span>
              {genres.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => applyGenre(g)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    genre === g
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label
                htmlFor="year-select"
                className="text-sm font-medium text-foreground-700"
              >
                年份
              </label>
              <select
                id="year-select"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y === "全部" ? "不限" : `${y} 年`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label
                htmlFor="sort-select"
                className="text-sm font-medium text-foreground-700"
              >
                排序
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
              >
                {sortLabels.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>

            <button
              type="button"
              onClick={() => setPanelOpen((v) => !v)}
              className="ml-auto flex items-center gap-1 rounded-md bg-secondary-500 px-3 py-1.5 text-sm font-medium text-background-50 md:hidden cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              类型筛选
            </button>
          </div>

          {panelOpen && (
            <div className="flex flex-wrap gap-2 rounded-md bg-background-50 p-3 md:hidden">
              {genres.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    applyGenre(g);
                    setPanelOpen(false);
                  }}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    genre === g
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-bold text-accent-600">{filtered.length}</span> 部
            {isMovie ? "电影" : "电视剧"}
          </p>
          <p className="text-xs text-foreground-500">
            第 {page} / {totalPages} 页
          </p>
        </div>

        {pageItems.length === 0 ? (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 py-20 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-700">
              未找到符合条件的{isMovie ? "电影" : "电视剧"}，尝试重置筛选。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {pageItems.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line"></i>
              上一页
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i + 1)}
                className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                  page === i + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer whitespace-nowrap"
            >
              下一页
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}